import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiCall } from '../api';
import logo from '../LinchpinCRTLOGOfinal.png';

// ── V3 TAG COLORS ──────────────────────────────────────────────────────────────
const TAG_COLORS_DARK = {
  0: { bg: '#1C2034', color: '#64748B', border: '#2A3350' },
  1: { bg: '#1A3A7A', color: '#93C5FD', border: '#3B82F6' },
  2: { bg: '#064E25', color: '#6EE7A0', border: '#22C55E' },
  3: { bg: '#2E1065', color: '#C4B5FD', border: '#A78BFA' },
  4: { bg: '#431407', color: '#FDB97D', border: '#F97316' },
  5: { bg: '#450A0A', color: '#FCA5A5', border: '#EF4444' },
  6: { bg: '#083344', color: '#67E8F9', border: '#06B6D4' },
};

const TAG_COLORS_LIGHT = {
  0: { bg: '#E4DED7', color: '#5A5042', border: '#BEB3A4' },
  1: { bg: '#BFDBFE', color: '#1E3A8A', border: '#93C5FD' },
  2: { bg: '#BBF7D0', color: '#14532D', border: '#86EFAC' },
  3: { bg: '#DDD6FE', color: '#4C1D95', border: '#C4B5FD' },
  4: { bg: '#FED7AA', color: '#7C2D12', border: '#FDBA74' },
  5: { bg: '#FECACA', color: '#7F1D1D', border: '#FCA5A5' },
  6: { bg: '#A5F3FC', color: '#164E63', border: '#67E8F9' },
};

const TAG_LABELS = {
  0: 'Untagged', 1: 'ATP Eval', 2: 'Approved',
  3: 'Pre-Auth', 4: 'In Auth', 5: 'Contingent Orders', 6: 'Intake W/DX',
};

const KEEP_COLS = ['Company','Customer','Primary Payer','Sales Person','Segment','Age','Value','Initiation','Base Code','State','Last Note','Created'];
const HEADER_ROW_IDX = 4;
const BAR_COLORS = ['#3B82F6','#22C55E','#A78BFA','#F97316','#06B6D4','#EF4444','#F59E0B','#EC4899'];
const INACTIVITY_LIMIT = 55 * 60 * 1000;
const WARN_DURATION = 5 * 60;

function FilterDropdown({ col, label, allVals, selected, setSelected, openDropdown, setOpenDropdown, refSort, onSort, surface, surface2, border, text, muted }) {
  const isOpen = openDropdown === col;
  const isActive = selected.length > 0;
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div onClick={e => { e.stopPropagation(); setOpenDropdown(isOpen ? null : col); }}
        style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', userSelect: 'none' }}>
        <span>{label}</span>
        <span style={{ color: isActive ? '#3B82F6' : muted, fontSize: '8px' }}>{refSort.col === col ? (refSort.dir > 0 ? '▲' : '▼') : '⇅'}</span>
        {isActive && <span style={{ background: '#3B82F6', color: '#fff', borderRadius: '8px', fontSize: '8px', padding: '0 4px', fontFamily: 'monospace' }}>{selected.length}</span>}
      </div>
      {isOpen && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 999, background: surface, border: `1px solid ${border}`, borderRadius: '6px', minWidth: '180px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', marginTop: '4px' }}
          onClick={e => e.stopPropagation()}>
          <div style={{ padding: '8px', borderBottom: `1px solid ${border}`, display: 'flex', gap: '6px' }}>
            <button onClick={() => setSelected(allVals)} style={{ fontFamily: 'monospace', fontSize: '9px', padding: '3px 8px', borderRadius: '3px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>All</button>
            <button onClick={() => setSelected([])} style={{ fontFamily: 'monospace', fontSize: '9px', padding: '3px 8px', borderRadius: '3px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>Clear</button>
            <button onClick={() => onSort(col)} style={{ fontFamily: 'monospace', fontSize: '9px', padding: '3px 8px', borderRadius: '3px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>
              Sort {refSort.col === col ? (refSort.dir > 0 ? '▲' : '▼') : '⇅'}
            </button>
          </div>
          <div style={{ maxHeight: '180px', overflowY: 'auto', padding: '4px' }}>
            {allVals.map(val => (
              <div key={val} onClick={() => setSelected(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val])}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', borderRadius: '4px', cursor: 'pointer', background: selected.includes(val) ? 'rgba(59,130,246,0.1)' : 'transparent' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '3px', border: `1px solid ${selected.includes(val) ? '#3B82F6' : border}`, background: selected.includes(val) ? '#3B82F6' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {selected.includes(val) && <span style={{ color: '#fff', fontSize: '9px', lineHeight: 1 }}>✓</span>}
                </div>
                <span style={{ fontSize: '11px', color: text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }} title={val}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RefRecordsTable({ refRecords, activeSPName, start, end, surface, surface2, border, text, muted }) {
  const [refSort, setRefSort] = useState({ col: null, dir: 1 });
  const [payerFilter, setPayerFilter] = useState([]);
  const [customerFilter, setCustomerFilter] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);

  const spRecs = refRecords.filter(r => {
    const d = new Date(r.atlas_created_at || r.created_at);
    return r.sales_person === activeSPName && d >= start && d <= end;
  });

  const allPayers = [...new Set(spRecs.map(r => r.primary_payer || '—'))].sort();
  const allCustomers = [...new Set(spRecs.map(r => r.customer || '—'))].sort();

  let displayRecs = spRecs.filter(r => {
    if (payerFilter.length > 0 && !payerFilter.includes(r.primary_payer || '—')) return false;
    if (customerFilter.length > 0 && !customerFilter.includes(r.customer || '—')) return false;
    return true;
  });

  if (refSort.col) {
    displayRecs = [...displayRecs].sort((a, b) => {
      if (refSort.col === 'value') {
        const av = parseFloat((a.value || '').replace(/[$,]/g, '')) || 0;
        const bv = parseFloat((b.value || '').replace(/[$,]/g, '')) || 0;
        return (av - bv) * refSort.dir;
      }
      const map = { enc: 'enc_num', company: 'company', customer: 'customer', payer: 'primary_payer', date: 'atlas_created_at' };
      const av = a[map[refSort.col]] || '';
      const bv = b[map[refSort.col]] || '';
      return String(av).localeCompare(String(bv)) * refSort.dir;
    });
  }

  function onSort(col) { setRefSort(s => s.col === col ? { col, dir: s.dir * -1 } : { col, dir: 1 }); }

  const hasFilters = payerFilter.length > 0 || customerFilter.length > 0;
  const ddProps = { openDropdown, setOpenDropdown, refSort, onSort, surface, surface2, border, text, muted };

  return (
    <div onClick={() => setOpenDropdown(null)} style={{ position: 'relative', zIndex: 20 }}>
      <div style={{ marginTop: '16px', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ fontFamily: 'monospace', fontSize: '9px', color: muted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Referral Records <span style={{ color: '#60A5FA' }}>({displayRecs.length})</span>
          </div>
          {hasFilters && (
            <button onClick={() => { setPayerFilter([]); setCustomerFilter([]); }}
              style={{ fontFamily: 'monospace', fontSize: '9px', padding: '2px 8px', borderRadius: '3px', border: '1px solid #EF4444', background: 'transparent', color: '#EF4444', cursor: 'pointer' }}>
              ✕ Clear Filters
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setOpenDropdown(openDropdown === 'customer' ? null : 'customer')}
              style={{ fontFamily: 'monospace', fontSize: '10px', padding: '4px 10px', borderRadius: '6px', border: `1px solid ${customerFilter.length > 0 ? '#3B82F6' : border}`, background: customerFilter.length > 0 ? 'rgba(59,130,246,0.1)' : surface2, color: customerFilter.length > 0 ? '#60A5FA' : muted, cursor: 'pointer' }}>
              Customer {customerFilter.length > 0 ? `(${customerFilter.length})` : '▾'}
            </button>
            {openDropdown === 'customer' && (
              <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 999, background: surface, border: `1px solid ${border}`, borderRadius: '6px', minWidth: '200px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)', marginTop: '4px' }}>
                <div style={{ padding: '6px 8px', borderBottom: `1px solid ${border}`, display: 'flex', gap: '6px' }}>
                  <button onClick={() => setCustomerFilter(allCustomers)} style={{ fontFamily: 'monospace', fontSize: '9px', padding: '2px 8px', borderRadius: '3px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>All</button>
                  <button onClick={() => setCustomerFilter([])} style={{ fontFamily: 'monospace', fontSize: '9px', padding: '2px 8px', borderRadius: '3px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>Clear</button>
                </div>
                <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '4px' }}>
                  {allCustomers.map(val => (
                    <div key={val} onClick={() => setCustomerFilter(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val])}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', borderRadius: '4px', cursor: 'pointer', background: customerFilter.includes(val) ? 'rgba(59,130,246,0.1)' : 'transparent' }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '3px', border: `1px solid ${customerFilter.includes(val) ? '#3B82F6' : border}`, background: customerFilter.includes(val) ? '#3B82F6' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {customerFilter.includes(val) && <span style={{ color: '#fff', fontSize: '9px', lineHeight: 1 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: '11px', color: text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }} title={val}>{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setOpenDropdown(openDropdown === 'payer' ? null : 'payer')}
              style={{ fontFamily: 'monospace', fontSize: '10px', padding: '4px 10px', borderRadius: '6px', border: `1px solid ${payerFilter.length > 0 ? '#3B82F6' : border}`, background: payerFilter.length > 0 ? 'rgba(59,130,246,0.1)' : surface2, color: payerFilter.length > 0 ? '#60A5FA' : muted, cursor: 'pointer' }}>
              Payer {payerFilter.length > 0 ? `(${payerFilter.length})` : '▾'}
            </button>
            {openDropdown === 'payer' && (
              <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 999, background: surface, border: `1px solid ${border}`, borderRadius: '6px', minWidth: '200px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)', marginTop: '4px' }}>
                <div style={{ padding: '6px 8px', borderBottom: `1px solid ${border}`, display: 'flex', gap: '6px' }}>
                  <button onClick={() => setPayerFilter(allPayers)} style={{ fontFamily: 'monospace', fontSize: '9px', padding: '2px 8px', borderRadius: '3px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>All</button>
                  <button onClick={() => setPayerFilter([])} style={{ fontFamily: 'monospace', fontSize: '9px', padding: '2px 8px', borderRadius: '3px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>Clear</button>
                </div>
                <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '4px' }}>
                  {allPayers.map(val => (
                    <div key={val} onClick={() => setPayerFilter(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val])}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', borderRadius: '4px', cursor: 'pointer', background: payerFilter.includes(val) ? 'rgba(59,130,246,0.1)' : 'transparent' }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '3px', border: `1px solid ${payerFilter.includes(val) ? '#3B82F6' : border}`, background: payerFilter.includes(val) ? '#3B82F6' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {payerFilter.includes(val) && <span style={{ color: '#fff', fontSize: '9px', lineHeight: 1 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: '11px', color: text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }} title={val}>{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ overflowY: 'auto', maxHeight: '260px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr>
                <th onClick={() => onSort('enc')} style={{ fontFamily: 'monospace', fontSize: '9px', fontWeight: '700', letterSpacing: '0.07em', textTransform: 'uppercase', color: refSort.col === 'enc' ? '#60A5FA' : muted, padding: '8px 12px', borderBottom: `1px solid ${border}`, textAlign: 'left', background: surface2, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                  Enc# {refSort.col === 'enc' ? (refSort.dir > 0 ? '▲' : '▼') : '⇅'}
                </th>
                <th onClick={() => onSort('company')} style={{ fontFamily: 'monospace', fontSize: '9px', fontWeight: '700', letterSpacing: '0.07em', textTransform: 'uppercase', color: refSort.col === 'company' ? '#60A5FA' : muted, padding: '8px 12px', borderBottom: `1px solid ${border}`, textAlign: 'left', background: surface2, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                  Company {refSort.col === 'company' ? (refSort.dir > 0 ? '▲' : '▼') : '⇅'}
                </th>
                <th style={{ fontFamily: 'monospace', fontSize: '9px', fontWeight: '700', letterSpacing: '0.07em', textTransform: 'uppercase', color: customerFilter.length > 0 ? '#3B82F6' : muted, padding: '8px 12px', borderBottom: `1px solid ${border}`, textAlign: 'left', background: surface2, whiteSpace: 'nowrap', userSelect: 'none' }}>
                  <FilterDropdown col="customer" label="Customer" allVals={allCustomers} selected={customerFilter} setSelected={setCustomerFilter} {...ddProps} />
                </th>
                <th style={{ fontFamily: 'monospace', fontSize: '9px', fontWeight: '700', letterSpacing: '0.07em', textTransform: 'uppercase', color: payerFilter.length > 0 ? '#3B82F6' : muted, padding: '8px 12px', borderBottom: `1px solid ${border}`, textAlign: 'left', background: surface2, whiteSpace: 'nowrap', userSelect: 'none' }}>
                  <FilterDropdown col="payer" label="Payer" allVals={allPayers} selected={payerFilter} setSelected={setPayerFilter} {...ddProps} />
                </th>
                <th onClick={() => onSort('value')} style={{ fontFamily: 'monospace', fontSize: '9px', fontWeight: '700', letterSpacing: '0.07em', textTransform: 'uppercase', color: refSort.col === 'value' ? '#60A5FA' : muted, padding: '8px 12px', borderBottom: `1px solid ${border}`, textAlign: 'left', background: surface2, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                  Value {refSort.col === 'value' ? (refSort.dir > 0 ? '▲' : '▼') : '⇅'}
                </th>
                <th onClick={() => onSort('date')} style={{ fontFamily: 'monospace', fontSize: '9px', fontWeight: '700', letterSpacing: '0.07em', textTransform: 'uppercase', color: refSort.col === 'date' ? '#60A5FA' : muted, padding: '8px 12px', borderBottom: `1px solid ${border}`, textAlign: 'left', background: surface2, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                  Date {refSort.col === 'date' ? (refSort.dir > 0 ? '▲' : '▼') : '⇅'}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayRecs.map((r, i, arr) => (
                <tr key={r.enc_num} style={{ borderBottom: i < arr.length - 1 ? `1px solid ${border}` : 'none' }}>
                  <td style={{ fontFamily: 'monospace', fontSize: '11px', color: muted, padding: '8px 12px', whiteSpace: 'nowrap' }}>{r.enc_num}</td>
                  <td style={{ fontSize: '12px', color: text, padding: '8px 12px', whiteSpace: 'nowrap', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.company}</td>
                  <td style={{ fontSize: '12px', color: text, padding: '8px 12px', whiteSpace: 'nowrap', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.customer}</td>
                  <td style={{ fontSize: '12px', color: text, padding: '8px 12px', whiteSpace: 'nowrap', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.primary_payer}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#60A5FA', fontWeight: 'bold', padding: '8px 12px', whiteSpace: 'nowrap' }}>{r.value}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '11px', color: muted, padding: '8px 12px', whiteSpace: 'nowrap' }}>{r.atlas_created_at ? new Date(r.atlas_created_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
              {displayRecs.length === 0 && (
                <tr><td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: muted, fontFamily: 'monospace', fontSize: '11px' }}>No records match filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AdminPanel({ user, darkMode, surface, surface2, border, text, muted, bg, onClose }) {
  const [companies, setCompanies] = useState([]);
  const [companyUsers, setCompanyUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newInitiation, setNewInitiation] = useState('');
  const [newManagerEmail, setNewManagerEmail] = useState('');
  // removed newManagerPassword
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('');
  const [addingCompany, setAddingCompany] = useState(false);
  const [addingManager, setAddingManager] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selectedViewCompany, setSelectedViewCompany] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    const cos = await apiCall('/admin/companies');
    setCompanies(Array.isArray(cos) ? cos : (cos?.companies || cos?.rows || []));
    const result = await apiCall('/admin/users');
    const grouped = {};
    (result.users || []).forEach(u => {
      if (!grouped[u.company_id]) grouped[u.company_id] = [];
      grouped[u.company_id].push(u);
    });
    setCompanyUsers(grouped);
    console.log('GROUPED USERS:', grouped);
    setLoading(false);
  }

  async function addCompany() {
    if (!newCompanyName.trim() || !newInitiation.trim()) {
      setMsg('Company name and initiation filter are required.'); setMsgType('error'); return;
    }
    setAddingCompany(true); setMsg('');
    const result = await apiCall('/admin/companies', {
      method: 'POST',
      body: JSON.stringify({ name: newCompanyName.trim(), initiationFilter: newInitiation.trim() })
    });
    setAddingCompany(false);
    if (result.error) { setMsg('Error: ' + result.error); setMsgType('error'); return; }
    setMsg('✅ Company added!'); setMsgType('success');
    setNewCompanyName(''); setNewInitiation('');
    loadAll();
  }

   async function addManager() {
    if (!newManagerEmail.trim() || !selectedCompanyId) {
      setMsg('Email and company are required.'); setMsgType('error'); return;
    }
    setAddingManager(true); setMsg('');
    const result = await apiCall('/email/invite', {
      method: 'POST',
      body: JSON.stringify({ email: newManagerEmail.trim(), company_id: parseInt(selectedCompanyId) })
    });
    setAddingManager(false);
    if (result.error) { setMsg('Error: ' + result.error); setMsgType('error'); return; }
    setMsg('✅ Manager invited! They will receive an email to set up their account.'); setMsgType('success');
    setNewManagerEmail(''); setSelectedCompanyId('');
    loadAll();
  }

  async function deleteCompany(company) {
    const result = await apiCall('/admin/delete-company', {
      method: 'POST',
      body: JSON.stringify({ company_id: company.id })
    });
    setConfirmDelete(null);
    if (result.error) { setMsg('Error: ' + result.error); setMsgType('error'); return; }
    setMsg('✅ Company deleted.'); setMsgType('success');
    loadAll();
  }

  async function deleteUser(u) {
    const result = await apiCall('/users/remove', {
      method: 'DELETE',
      body: JSON.stringify({ user_id: u.id, auth_id: u.auth_id })
    });
    setConfirmDelete(null);
    if (result.error) { setMsg('Error: ' + result.error); setMsgType('error'); return; }
    setMsg('✅ User removed.'); setMsgType('success');
    loadAll();
  }

  const inputStyle = {
    background: bg, border: `1px solid ${border}`, borderRadius: '6px',
    padding: '8px 12px', color: text, fontFamily: 'monospace',
    fontSize: '12px', outline: 'none', width: '100%'
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, backdropFilter: 'blur(4px)' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: '10px', padding: '28px', width: '620px', maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto' }}>
        <div style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: '700', marginBottom: '4px', color: darkMode ? '#C4B5FD' : '#4C1D95' }}>⚡ Super Admin</div>
        <div style={{ color: muted, fontSize: '12px', marginBottom: '24px' }}>Manage companies and managers</div>
        <div style={{ fontFamily: 'monospace', fontSize: '9px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px', fontWeight: '700' }}>All Companies</div>
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center', color: muted, fontFamily: 'monospace', fontSize: '12px' }}>Loading...</div>
        ) : (
          <div style={{ marginBottom: '16px' }}>
            <select value={selectedViewCompany} onChange={e => setSelectedViewCompany(e.target.value)}
              style={{ background: bg, border: `1px solid ${border}`, borderRadius: '6px', padding: '8px 12px', color: text, fontFamily: 'monospace', fontSize: '12px', outline: 'none', width: '100%', marginBottom: '12px', cursor: 'pointer' }}>
              <option value="">Select a company to view...</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({(companyUsers[String(c.id)] || companyUsers[c.id] || []).length} users)</option>)}
            </select>
            {selectedViewCompany && (() => {
              const c = companies.find(co => String(co.id) === String(selectedViewCompany));
              if (!c) return null;
              return (
                <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: '8px', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: `1px solid ${border}`, background: surface2 }}>
                    <div>
                      <div style={{ fontSize: '13px', color: text, fontFamily: 'monospace', fontWeight: '700' }}>{c.name}</div>
                      <div style={{ fontSize: '10px', color: muted, fontFamily: 'monospace', marginTop: '2px' }}>
                        Initiation: <span style={{ color: darkMode ? '#93C5FD' : '#1D4ED8' }}>{c.settings?.initiationFilter || '—'}</span> · ID: {c.id}
                      </div>
                    </div>
                    <button onClick={() => setConfirmDelete({ type: 'company', item: c })}
                      style={{ fontFamily: 'monospace', fontSize: '10px', fontWeight: '700', padding: '4px 10px', borderRadius: '5px', border: '1px solid #EF4444', background: 'transparent', color: '#EF4444', cursor: 'pointer' }}>
                      Delete Company
                    </button>
                  </div>
                  {confirmDelete?.type === 'company' && confirmDelete.item.id === c.id && (
                    <div style={{ padding: '10px 14px', background: darkMode ? '#1A0A0A' : '#FEF2F2', borderBottom: `1px solid #EF4444` }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '11px', color: darkMode ? '#FCA5A5' : '#B91C1C', marginBottom: '8px' }}>
                        ⚠️ Delete <strong>{c.name}</strong>? This removes ALL users and records. Cannot be undone.
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => setConfirmDelete(null)} style={{ fontFamily: 'monospace', fontSize: '10px', padding: '4px 12px', borderRadius: '5px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>Cancel</button>
                        <button onClick={() => deleteCompany(c)} style={{ fontFamily: 'monospace', fontSize: '10px', fontWeight: '700', padding: '4px 12px', borderRadius: '5px', border: 'none', background: '#EF4444', color: '#fff', cursor: 'pointer' }}>Confirm Delete</button>
                      </div>
                    </div>
                  )}
                  {(companyUsers[String(c.id)] || companyUsers[c.id] || []).length === 0 ? (
                    <div style={{ padding: '10px 14px', color: muted, fontFamily: 'monospace', fontSize: '11px' }}>No users yet</div>
                  ) : ((companyUsers[String(c.id)] || companyUsers[c.id] || [])).map((u, i, arr) => (
                    <div key={u.id}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderBottom: i < arr.length - 1 ? `1px solid ${border}` : 'none' }}>
                        <div>
                          <div style={{ fontSize: '12px', color: text, fontFamily: 'monospace' }}>{u.email}</div>
                          <div style={{ fontSize: '10px', color: u.role === 'manager' ? (darkMode ? '#93C5FD' : '#1D4ED8') : u.role === 'super_admin' ? (darkMode ? '#C4B5FD' : '#4C1D95') : '#22C55E', fontFamily: 'monospace', marginTop: '1px', fontWeight: '600' }}>{u.role}</div>
                        </div>
                        {u.email !== user.email && (
                          <button onClick={() => setConfirmDelete({ type: 'user', item: u })}
                            style={{ fontFamily: 'monospace', fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '5px', border: '1px solid #EF4444', background: 'transparent', color: '#EF4444', cursor: 'pointer' }}>
                            Remove
                          </button>
                        )}
                      </div>
                      {confirmDelete?.type === 'user' && confirmDelete.item.id === u.id && (
                        <div style={{ padding: '8px 14px', background: darkMode ? '#1A0A0A' : '#FEF2F2', borderBottom: i < arr.length - 1 ? `1px solid ${border}` : 'none' }}>
                          <div style={{ fontFamily: 'monospace', fontSize: '11px', color: darkMode ? '#FCA5A5' : '#B91C1C', marginBottom: '8px' }}>
                            Remove <strong>{u.email}</strong>? Cannot be undone.
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => setConfirmDelete(null)} style={{ fontFamily: 'monospace', fontSize: '10px', padding: '4px 12px', borderRadius: '5px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>Cancel</button>
                            <button onClick={() => deleteUser(u)} style={{ fontFamily: 'monospace', fontSize: '10px', fontWeight: '700', padding: '4px 12px', borderRadius: '5px', border: 'none', background: '#EF4444', color: '#fff', cursor: 'pointer' }}>Confirm Remove</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
        <div style={{ fontFamily: 'monospace', fontSize: '9px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px', marginTop: '8px', fontWeight: '700' }}>Add New Company</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
          <input style={inputStyle} placeholder="Company name" value={newCompanyName} onChange={e => setNewCompanyName(e.target.value)} />
          <input style={inputStyle} placeholder="Initiation filter word (e.g. Sales Complex Mobility)" value={newInitiation} onChange={e => setNewInitiation(e.target.value)} />
          <button onClick={addCompany} disabled={addingCompany}
            style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: '700', padding: '8px 16px', borderRadius: '6px', border: 'none', background: darkMode ? '#2E1065' : '#4C1D95', color: '#C4B5FD', cursor: 'pointer', opacity: addingCompany ? 0.6 : 1, alignSelf: 'flex-start' }}>
            {addingCompany ? 'Adding...' : '+ Add Company'}
          </button>
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: '9px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px', fontWeight: '700' }}>Add Manager to Company</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
          <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select company...</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input style={inputStyle} placeholder="Manager email" value={newManagerEmail} onChange={e => setNewManagerEmail(e.target.value)} />
           
          <button onClick={addManager} disabled={addingManager}
            style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: '700', padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#2563EB', color: '#fff', cursor: 'pointer', opacity: addingManager ? 0.6 : 1, alignSelf: 'flex-start' }}>
            {addingManager ? 'Adding...' : '+ Add Manager'}
          </button>
        </div>
        {msg && (
          <div style={{ fontFamily: 'monospace', fontSize: '11px', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', background: msgType === 'success' ? '#064E25' : '#450A0A', border: `1px solid ${msgType === 'success' ? '#22C55E' : '#EF4444'}`, color: msgType === 'success' ? '#6EE7A0' : '#FCA5A5' }}>
            {msg}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ fontFamily: 'monospace', fontSize: '11px', padding: '7px 14px', borderRadius: '6px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState('enc');
  const [records, setRecords] = useState([]);
  const [tags, setTags] = useState({});
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  const [lastImportTime, setLastImportTime] = useState(null);
  const [frozenEncNums, setFrozenEncNums] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState(1);
  const [showImport, setShowImport] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [darkMode, setDarkMode] = useState(false);
  const [visibleCols] = useState(KEEP_COLS);
  const [importMsg, setImportMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importedNew, setImportedNew] = useState(new Set());
  const [importedUpdated, setImportedUpdated] = useState(new Set());
  const [refRecords, setRefRecords] = useState([]);
  const [refLoading, setRefLoading] = useState(false);
  const [refLoaded, setRefLoaded] = useState(false);
  const [periodType, setPeriodType] = useState('month');
  const [periodOffset, setPeriodOffset] = useState(0);
  const [selectedSP, setSelectedSP] = useState(null);
  const [overviewCollapsed, setOverviewCollapsed] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionCountdown, setSessionCountdown] = useState(WARN_DURATION);
  const [initiationFilterValue] = useState('Sales Complex Mobility');
  const [encOpenDropdown, setEncOpenDropdown] = useState(null);
  const [companyFilter, setCompanyFilter] = useState([]);
  const [initiationFilter, setInitiationFilter] = useState([]);
  const [payerEncFilter, setPayerEncFilter] = useState([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [spFilter, setSpFilter] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMsg, setInviteMsg] = useState('');
  const [confirmRemoveUser, setConfirmRemoveUser] = useState(null);

  const inactivityTimer = useRef(null);
  const countdownInterval = useRef(null);

  const user = (() => { try { return JSON.parse(localStorage.getItem('token') || '{}'); } catch { return {}; } })();
  const isManager = user.role === 'manager' || user.role === 'super_admin';
  const isSuperAdmin = user.role === 'super_admin';

  function logout() { 
    localStorage.removeItem('token'); 
    localStorage.removeItem('access_token');
    window.location.href = '/login'; 
  }

  const resetInactivityTimer = useCallback(() => {
    if (showSessionModal) return;
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      setShowSessionModal(true);
      setSessionCountdown(WARN_DURATION);
    }, INACTIVITY_LIMIT);
  }, [showSessionModal]);

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetInactivityTimer));
    resetInactivityTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetInactivityTimer));
      clearTimeout(inactivityTimer.current);
    };
  }, [resetInactivityTimer]);

  useEffect(() => {
    if (showSessionModal) {
      countdownInterval.current = setInterval(() => {
        setSessionCountdown(prev => {
          if (prev <= 1) { clearInterval(countdownInterval.current); logout(); return 0; }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(countdownInterval.current);
    }
    return () => clearInterval(countdownInterval.current);
  }, [showSessionModal]); // eslint-disable-line react-hooks/exhaustive-deps

  function continueSession() {
    setShowSessionModal(false);
    setSessionCountdown(WARN_DURATION);
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      setShowSessionModal(true);
      setSessionCountdown(WARN_DURATION);
    }, INACTIVITY_LIMIT);
  }

  function formatCountdown(secs) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  useEffect(() => {
    document.title = user.company ? `Linchpin · ${user.company}` : 'Linchpin';
  }, [user.company]);

  useEffect(() => {
    loadRecords();
    loadCompanySettings();
    if (!isManager) {
      const interval = setInterval(() => { loadRecords(); }, 30000);
      return () => clearInterval(interval);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'referrals' && !refLoaded) loadRefRecords();
  }, [activeTab, refLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadRecords() {
    setLoading(true);
    const data = await apiCall('/enc-records');
    if (!Array.isArray(data)) { setLoading(false); return; }
    const recs = (data || []).map(r => ({
      __enc: r.enc_num, Company: r.company, Customer: r.customer,
      'Primary Payer': r.primary_payer, 'Sales Person': r.sales_person,
      Segment: r.segment, Age: r.age, Value: r.value, Initiation: r.initiation,
      'Base Code': r.base_code, State: r.state, 'Last Note': r.last_note,
      Created: r.atlas_created_at || '', _isNew: false, _updated: false,
    }));
    const tagMap = {};
    (data || []).forEach(r => { tagMap[r.enc_num] = r.tag || 0; });
    setRecords(recs); setTags(tagMap); setLoading(false);
  }

  async function loadRefRecords() {
    setRefLoading(true);
    const data = await apiCall(`/ref-records?initiation=${encodeURIComponent(initiationFilterValue)}`);
    if (!Array.isArray(data)) { setRefLoading(false); setRefLoaded(true); return; }
setRefRecords(data || []); setRefLoading(false); setRefLoaded(true);
  }

  async function loadCompanySettings() {}

  async function loadCompanyUsers() {
    setUsersLoading(true);
    const result = await apiCall('/users');
    setUsersLoading(false);
    if (result.error) { console.error(result.error); return; }
    const sorted = (result.users || []).sort((a, b) => {
      if (a.email === user.email) return -1;
      if (b.email === user.email) return 1;
      if (a.role === 'manager' && b.role !== 'manager') return -1;
      if (b.role === 'manager' && a.role !== 'manager') return 1;
      return 0;
    });
    setCompanyUsers(sorted);
  }

  async function inviteUser() {
    if (!inviteEmail.trim()) return;
    const emails = inviteEmail.split(',').map(e => e.trim()).filter(e => e);
    if (emails.length === 0) return;
    setInviteLoading(true); setInviteMsg('');
    let successCount = 0;
    let errors = [];
    for (let i = 0; i < emails.length; i++) {
      const email = emails[i];
      setInviteMsg(`Sending ${i + 1} of ${emails.length}: ${email}...`);
      try {
        const result = await apiCall('/email/invite', {
          method: 'POST',
          body: JSON.stringify({ email })
        });
        if (result.error) { errors.push(`${email}: ${result.error}`); }
        else { successCount++; }
      } catch (e) {
        errors.push(`${email}: ${e.message}`);
      }
    }
    setInviteLoading(false);
    if (errors.length === 0) {
      setInviteMsg(`✅ ${successCount} invite${successCount !== 1 ? 's' : ''} sent!`);
    } else if (successCount > 0) {
      setInviteMsg(`✅ ${successCount} sent · ❌ ${errors.length} failed: ${errors.join(', ')}`);
    } else {
      setInviteMsg(`❌ All failed: ${errors.join(', ')}`);
    }
    setInviteEmail('');
    loadCompanyUsers();
  }

  async function removeUser(u) {
    const result = await apiCall('/users/remove', {
      method: 'DELETE',
      body: JSON.stringify({ user_id: u.id, auth_id: u.auth_id })
    });
    setConfirmRemoveUser(null);
    if (result.error) { alert('Error: ' + result.error); return; }
    loadCompanyUsers();
  }

  async function changePassword() {
    if (!currentPassword) { setPasswordMsg('Please enter your current password.'); return; }
    if (newPassword !== confirmPassword) { setPasswordMsg('Passwords do not match.'); return; }
    if (newPassword.length < 8) { setPasswordMsg('Must be at least 8 characters.'); return; }
    if (!/[A-Z]/.test(newPassword)) { setPasswordMsg('Must contain an uppercase letter.'); return; }
    if (!/[0-9]/.test(newPassword)) { setPasswordMsg('Must contain a number.'); return; }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword)) { setPasswordMsg('Must contain a special character.'); return; }
    setPasswordLoading(true); setPasswordMsg('');
    const result = await apiCall('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword })
    });
    setPasswordLoading(false);
    if (result.error) { setPasswordMsg('Error: ' + result.error); return; }
    setPasswordMsg('✅ Password updated successfully!');
    setTimeout(() => { setShowChangePassword(false); setNewPassword(''); setConfirmPassword(''); setCurrentPassword(''); setPasswordMsg(''); }, 2000);
  }

  function getPeriodBounds(offset, type) {
    const now = new Date();
    let start, end, prevStart, prevEnd, label, prevLabel;
    if (type === 'month') {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      start = new Date(d.getFullYear(), d.getMonth(), 1);
      end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
      const p = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      prevStart = new Date(p.getFullYear(), p.getMonth(), 1);
      prevEnd = new Date(p.getFullYear(), p.getMonth() + 1, 0, 23, 59, 59);
      label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
      prevLabel = p.toLocaleString('default', { month: 'long', year: 'numeric' });
    } else if (type === 'quarter') {
      const bq = Math.floor(now.getMonth() / 3) + offset;
      const yr = now.getFullYear() + Math.floor(bq / 4);
      const q = ((bq % 4) + 4) % 4;
      start = new Date(yr, q * 3, 1); end = new Date(yr, q * 3 + 3, 0, 23, 59, 59);
      prevStart = new Date(yr, q * 3 - 3, 1); prevEnd = new Date(yr, q * 3, 0, 23, 59, 59);
      label = `Q${q + 1} ${yr}`; prevLabel = `Q${((q - 1 + 4) % 4) + 1} ${q === 0 ? yr - 1 : yr}`;
    } else {
      const yr = now.getFullYear() + offset;
      start = new Date(yr, 0, 1); end = new Date(yr, 11, 31, 23, 59, 59);
      prevStart = new Date(yr - 1, 0, 1); prevEnd = new Date(yr - 1, 11, 31, 23, 59, 59);
      label = `${yr}`; prevLabel = `${yr - 1}`;
    }
    return { start, end, prevStart, prevEnd, label, prevLabel };
  }

  function filterRefRecs(start, end) {
    const seen = new Set();
    return refRecords.filter(r => {
      if (seen.has(r.enc_num)) return false;
      const d = new Date(r.atlas_created_at || r.created_at);
      if (d >= start && d <= end) { seen.add(r.enc_num); return true; }
      return false;
    });
  }

  function groupBySP(recs) {
    const map = {};
    recs.forEach(r => {
      const sp = r.sales_person || 'Unknown';
      if (!map[sp]) map[sp] = { refs: 0, value: 0, insurance: {} };
      map[sp].refs++;
      map[sp].value += parseFloat((r.value || '').replace(/[$,]/g, '')) || 0;
      const pay = r.primary_payer || 'Unknown';
      map[sp].insurance[pay] = (map[sp].insurance[pay] || 0) + 1;
    });
    return map;
  }

  function splitLine(line) {
    const out = []; let cur = ''; let q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
      else if (ch === ',' && !q) { out.push(cur); cur = ''; }
      else cur += ch;
    }
    out.push(cur); return out;
  }

  function fuzzyFind(target, headers) {
    const t = target.toLowerCase().replace(/[^a-z0-9]/g, '');
    let f = headers.find(h => h.toLowerCase().replace(/[^a-z0-9]/g, '') === t);
    if (f) return f;
    f = headers.find(h => h.toLowerCase().includes(target.toLowerCase()));
    return f || null;
  }

  async function importCSV() {
    setImporting(true); setImportMsg('');
    const allLines = pasteText.split(/\r?\n/);
    let headerIdx = HEADER_ROW_IDX;
    for (let i = 0; i < Math.min(8, allLines.length); i++) {
      const l = allLines[i].toLowerCase();
      if (l.includes('enc') && l.includes('company')) { headerIdx = i; break; }
    }
    if (allLines.length <= headerIdx) { setImportMsg('Could not read CSV.'); setImporting(false); return; }
    const headers = splitLine(allLines[headerIdx]).map(h => h.trim().replace(/^"|"$/g, ''));
    const encCol = fuzzyFind('Enc#', headers) || fuzzyFind('Enc', headers) || headers[1];
    const colMap = {};
    KEEP_COLS.forEach(label => { colMap[label] = fuzzyFind(label, headers); });
    const oldMap = {}; records.forEach(r => oldMap[r.__enc] = r);
    const next = [];
    for (let i = headerIdx + 1; i < allLines.length; i++) {
      const line = allLines[i]; if (!line.trim()) continue;
      const vals = splitLine(line).map(v => v.trim().replace(/^"|"$/g, ''));
      if (vals.every(v => !v)) continue;
      const row = {}; headers.forEach((h, idx) => { row[h] = vals[idx] ?? ''; });
      const enc = (row[encCol] || '').trim(); if (!enc) continue;
      const rec = { __enc: enc };
      KEEP_COLS.forEach(label => { const src = colMap[label]; rec[label] = src ? (row[src] || '') : ''; });
      rec._isNew = !oldMap[enc];
      rec._updated = oldMap[enc] ? KEEP_COLS.filter(l => l !== 'Created').some(l => (oldMap[enc][l] || '') !== (rec[l] || '')) : false;
      next.push(rec);
    }
    if (!next.length) { setImportMsg('No records found.'); setImporting(false); return; }

    const upsertData = next.map(r => ({
      enc_num: r.__enc, company: r['Company'] || '',
      customer: r['Customer'] || '', primary_payer: r['Primary Payer'] || '',
      sales_person: r['Sales Person'] || '', segment: r['Segment'] || '',
      age: r['Age'] || '', value: r['Value'] || '', initiation: r['Initiation'] || '',
      base_code: r['Base Code'] || '', state: r['State'] || '', last_note: r['Last Note'] || '',
      atlas_created_at: (() => {
        const parts = (r['Created'] || '').split('/');
        if (parts.length === 3) return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        return null;
      })(),
      tag: tags[r.__enc] || 0,
    }));

    const result = await apiCall('/enc-records/upsert', {
      method: 'POST',
      body: JSON.stringify({ records: upsertData })
    });

    if (result.error) { setImportMsg('Error saving: ' + result.error); setImporting(false); return; }

    const newCount = next.filter(r => r._isNew).length;
    const updatedCount = next.filter(r => r._updated).length;
    const summaryMsg = `✅ ${next.length} records imported · ${newCount} new · ${updatedCount} updated`;

    setPasteText('');
    setImportMsg(summaryMsg);
    setImporting(false);
    setImportedNew(new Set(next.filter(r => r._isNew).map(r => r.__enc)));
    setImportedUpdated(new Set(next.filter(r => r._updated).map(r => r.__enc)));
    setRefLoaded(false);
    loadRecords();
    setLastImportTime(new Date());
    setTimeout(() => { setShowImport(false); setImportMsg(''); }, 3000);
  }

  async function setTag(enc, val) {
    const n = parseInt(val);
    const tagVal = (isNaN(n) || n < 0 || n > 6) ? 0 : n;
    setTags(prev => ({ ...prev, [enc]: tagVal }));
    await apiCall('/enc-records/tag', {
      method: 'PATCH',
      body: JSON.stringify({ enc_num: enc, tag: tagVal })
    });
  }

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d * -1);
    else { setSortCol(col); setSortDir(1); }
  }

  function parseVal(s) {
    if (!s) return 0;
    const n = parseFloat(s.replace(/[$,\s]/g, ''));
    return isNaN(n) ? 0 : n;
  }

  function fmt(n) {
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  const summary = {};
  for (let i = 0; i <= 6; i++) summary[i] = { count: 0, total: 0 };
  let grand = 0;
  records.forEach(r => {
    const tag = tags[r.__enc] || 0;
    summary[tag].count++;
    const v = parseVal(r['Value']);
    summary[tag].total += v; grand += v;
  });

  let filtered = records.filter(r => {
    if (frozenEncNums !== null && !frozenEncNums.has(r.__enc)) return false;
    if (search) {
      const hay = [r.__enc, ...Object.values(r)].join(' ').toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    if (companyFilter.length > 0 && !companyFilter.includes(r['Company'] || '—')) return false;
    if (initiationFilter.length > 0 && !initiationFilter.includes(r['Initiation'] || '—')) return false;
    if (payerEncFilter.length > 0 && !payerEncFilter.includes(r['Primary Payer'] || '—')) return false;
    if (spFilter.length > 0 && !spFilter.includes(r['Sales Person'] || '—')) return false;
    return true;
  });

  if (sortCol) {
    filtered = [...filtered].sort((a, b) => {
      const av = (sortCol === '__enc' ? a.__enc : a[sortCol]) || '';
      const bv = (sortCol === '__enc' ? b.__enc : b[sortCol]) || '';
      const an = parseVal(av), bn = parseVal(bv);
      if (an || bn) return (an - bn) * sortDir;
      return String(av).localeCompare(String(bv)) * sortDir;
    });
  }

  const bg       = darkMode ? '#0B0D15' : '#F5F0E8';
  const surface  = darkMode ? '#10121E' : '#EDE7DD';
  const surface2 = darkMode ? '#1C2034' : '#E0D9D0';
  const border   = darkMode ? '#2A3350' : '#BEB3A4';
  const text     = darkMode ? '#E2E8F0' : '#0F1C2E';
  const muted    = darkMode ? '#4B5578' : '#7A6E60';
  const TC = darkMode ? TAG_COLORS_DARK : TAG_COLORS_LIGHT;

  const { start, end, prevStart, prevEnd, label, prevLabel } = getPeriodBounds(periodOffset, periodType);
  const curRefs = groupBySP(filterRefRecs(start, end));
  const prevRefs = groupBySP(filterRefRecs(prevStart, prevEnd));
  const sortedSP = Object.entries(curRefs).sort((a, b) => b[1].refs - a[1].refs);
  const totalRefs = sortedSP.reduce((s, [, d]) => s + d.refs, 0);
  const activeSPName = selectedSP || (sortedSP[0] ? sortedSP[0][0] : null);
  const activeSPData = activeSPName ? curRefs[activeSPName] : null;
  const activeSPPrev = activeSPName ? ((prevRefs[activeSPName] || {}).refs || 0) : 0;

  const btnStyle = () => ({
    fontFamily: 'monospace', fontSize: '11px', padding: '6px 12px',
    borderRadius: '6px', border: `1px solid ${border}`,
    background: surface2, color: text, cursor: 'pointer', fontWeight: '600',
  });

  return (
    <div style={{ minHeight: '100vh', background: bg, fontFamily: "'IBM Plex Sans', sans-serif", color: text }}>

      {showSessionModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400, backdropFilter: 'blur(6px)' }}>
          <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: '12px', padding: '36px', width: '380px', maxWidth: '95vw', textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>⏱</div>
            <div style={{ fontFamily: 'monospace', fontSize: '15px', fontWeight: 'bold', color: '#F97316', marginBottom: '8px' }}>Session Expiring</div>
            <div style={{ fontSize: '12px', color: muted, lineHeight: 1.7, marginBottom: '20px' }}>You've been inactive. Your session will end in</div>
            <div style={{ fontFamily: 'monospace', fontSize: '40px', fontWeight: 'bold', color: sessionCountdown <= 60 ? '#EF4444' : '#F97316', marginBottom: '24px', letterSpacing: '0.05em' }}>{formatCountdown(sessionCountdown)}</div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={logout} style={{ fontFamily: 'monospace', fontSize: '12px', padding: '10px 20px', borderRadius: '6px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>Log Out</button>
              <button onClick={continueSession} style={{ fontFamily: 'monospace', fontSize: '12px', padding: '10px 24px', borderRadius: '6px', border: 'none', background: '#2563EB', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>Continue Session</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ background: surface, borderBottom: `1px solid ${border}`, padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src={logo} alt="LinchpinCRT" style={{ height: '30px', width: '30px', objectFit: 'contain', borderRadius: '7px', background: '#1B4F8A' }} />
          <span style={{ fontFamily: 'monospace', fontWeight: '700', fontSize: '14px', color: darkMode ? '#E2E8F0' : '#0F1C2E', letterSpacing: '0.03em' }}>LinchpinCRT</span>
          {user.company && <span style={{ fontFamily: 'monospace', fontSize: '18px', color: muted }}>· {user.company}</span>}
          {lastImportTime && <span style={{ fontFamily: 'monospace', fontSize: '10px', color: muted }}>· Last import: {lastImportTime.toLocaleString()}</span>}
          <div style={{ fontFamily: 'monospace', fontSize: '9px', padding: '2px 8px', borderRadius: '20px', background: isSuperAdmin ? '#2E1065' : isManager ? '#1A3A7A' : '#064E25', color: isSuperAdmin ? '#C4B5FD' : isManager ? '#93C5FD' : '#6EE7A0', border: `1px solid ${isSuperAdmin ? '#A78BFA' : isManager ? '#3B82F6' : '#22C55E'}`, fontWeight: '700', letterSpacing: '0.05em' }}>
            {isSuperAdmin ? 'SUPER ADMIN' : isManager ? 'MANAGER' : 'VIEWER'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <input style={{ fontFamily: 'monospace', fontSize: '11px', background: surface2, border: `1px solid ${border}`, borderRadius: '6px', color: text, padding: '6px 12px', outline: 'none', width: '200px' }}
            placeholder="Search name, enc#, payer..." value={search} onChange={e => setSearch(e.target.value)} />
          {isManager && activeTab === 'enc' && (
            <button onClick={() => setShowImport(true)} style={{ fontFamily: 'monospace', fontSize: '11px', padding: '7px 14px', borderRadius: '6px', border: 'none', background: '#2563EB', color: '#fff', cursor: 'pointer', fontWeight: '700', whiteSpace: 'nowrap' }}>⊕ Import CSV</button>
          )}
          <button onClick={() => setDarkMode(d => !d)} style={btnStyle()}>{darkMode ? '🌙 Dark' : '☀ Light'}</button>
          {isManager && <button onClick={() => { setShowUserManagement(true); loadCompanyUsers(); }} style={btnStyle()}>👥 Users</button>}
          {isSuperAdmin && <button onClick={() => setShowAdminPanel(true)} style={{ ...btnStyle(), color: darkMode ? '#C4B5FD' : '#4C1D95', borderColor: darkMode ? '#A78BFA' : '#C4B5FD' }}>⚡ Admin</button>}
          <button onClick={() => setShowChangePassword(true)} style={btnStyle()}>⚙ Account</button>
          <button onClick={logout} style={{ ...btnStyle(), color: darkMode ? '#FCA5A5' : '#B91C1C', background: darkMode ? '#3B0A0A' : '#FEE2E2', borderColor: darkMode ? '#7F1D1D' : '#FCA5A5' }}>Sign Out</button>
        </div>
      </div>

      <div style={{ background: surface, borderBottom: `2px solid ${border}`, display: 'flex', padding: '10px 20px 0', gap: '6px' }}>
        {[{ id: 'enc', label: '📋  ENC Report' }, { id: 'referrals', label: '📊  Sales Report' }].map(t => (
          <div key={t.id} onClick={() => setActiveTab(t.id)} style={{
            fontFamily: 'monospace', fontSize: '11px', fontWeight: '700',
            padding: '7px 20px', cursor: 'pointer', letterSpacing: '0.03em',
            borderRadius: '8px 8px 0 0',
            background: activeTab === t.id ? '#2563EB' : surface2,
            color: activeTab === t.id ? '#fff' : muted,
            transition: 'all 0.15s',
          }}>{t.label}</div>
        ))}
      </div>

      {activeTab === 'enc' && (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 115px)', overflow: 'hidden' }}>
          <div style={{ background: surface, borderBottom: `1px solid ${border}`, padding: '10px 20px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'stretch' }}>
            {[1, 2, 3, 4, 5, 6, 0].map(i => {
              const tc = TC[i]; const isActive = activeFilter === i;
              return (
                <div key={i} onClick={e => {
                  e.stopPropagation();
                  const next = activeFilter === i ? null : i;
                  setActiveFilter(next);
                  if (next !== null) {
                    const encsInFilter = records.filter(r => (tags[r.__enc] || 0) === next).map(r => r.__enc);
                    setFrozenEncNums(new Set(encsInFilter));
                  } else {
                    setFrozenEncNums(null);
                  }
                }}
                  style={{ display: 'flex', flexDirection: 'column', padding: '8px 12px', borderRadius: '8px', background: tc.bg, color: tc.color, cursor: 'pointer', minWidth: '100px', border: isActive ? `2px solid ${tc.border}` : `1px solid ${tc.border}`, transition: 'all 0.1s', opacity: activeFilter !== null && !isActive ? 0.45 : 1, boxShadow: isActive ? `0 0 0 2px ${tc.border}40` : 'none' }}>
                  <div style={{ fontSize: '10px', fontFamily: 'monospace', letterSpacing: '0.04em', marginBottom: '4px', fontWeight: '800', opacity: 1, textTransform: 'uppercase' }}>
                    {i === 0 ? 'UNTAGGED' : `${i} · ${TAG_LABELS[i].slice(0, 11).toUpperCase()}`}
                  </div>
                  <div style={{ fontFamily: 'monospace', fontWeight: '800', fontSize: '13px', lineHeight: 1 }}>{fmt(summary[i].total)}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '11px', opacity: 1, marginTop: '3px', fontWeight: '700' }}>{summary[i].count} rec</div>
                </div>
              );
            })}
            <div style={{ marginLeft: 'auto', background: darkMode ? '#111932' : '#0F172A', border: `1px solid ${darkMode ? '#2563EB55' : '#1D4ED8'}`, borderRadius: '8px', padding: '8px 14px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: '120px' }}>
              <div style={{ fontFamily: 'monospace', fontSize: '8px', color: darkMode ? '#4B5578' : '#93C5FD', letterSpacing: '0.08em', fontWeight: '700' }}>GRAND TOTAL</div>
              <div style={{ fontFamily: 'monospace', fontWeight: '800', fontSize: '16px', color: '#93C5FD' }}>{fmt(grand)}</div>
              <div style={{ fontFamily: 'monospace', fontSize: '9px', color: darkMode ? '#4B5578' : '#93C5FD', fontWeight: '600' }}>{records.length} records</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', padding: '7px 20px', background: bg, borderBottom: `1px solid ${border}`, alignItems: 'center' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '8px', color: muted, marginRight: '4px', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: '700' }}>KEY:</span>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontFamily: 'monospace', fontSize: '10px', fontWeight: '700', padding: '3px 9px', borderRadius: '20px', background: TC[i].bg, color: TC[i].color }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: TC[i].color }} />
                {i} = {TAG_LABELS[i]}
              </div>
            ))}
          </div>

          {records.length > 0 && (() => {
            const allCompanies = [...new Set(records.map(r => r['Company'] || '—'))].sort();
            const allInitiations = [...new Set(records.map(r => r['Initiation'] || '—'))].sort();
            const allPayersEnc = [...new Set(records.map(r => r['Primary Payer'] || '—'))].sort();
            const allSPs = [...new Set(records.map(r => r['Sales Person'] || '—'))].sort();
            const hasEncFilters = companyFilter.length > 0 || initiationFilter.length > 0 || payerEncFilter.length > 0 || spFilter.length > 0;
            const filterCols = [
              { key: 'company', label: 'Company', allVals: allCompanies, selected: companyFilter, setSelected: setCompanyFilter },
              { key: 'payer', label: 'Payer', allVals: allPayersEnc, selected: payerEncFilter, setSelected: setPayerEncFilter },
              { key: 'sp', label: 'Sales Person', allVals: allSPs, selected: spFilter, setSelected: setSpFilter },
              { key: 'initiation', label: 'Initiation', allVals: allInitiations, selected: initiationFilter, setSelected: setInitiationFilter },
            ];
            return (
              <div style={{ padding: '7px 20px', background: surface, borderBottom: `1px solid ${border}`, display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }} onClick={() => setEncOpenDropdown(null)}>
                {filterCols.map(fc => (
                  <div key={fc.key} style={{ position: 'relative' }}>
                    <button onClick={e => { e.stopPropagation(); setEncOpenDropdown(encOpenDropdown === fc.key ? null : fc.key); }}
                      style={{ fontFamily: 'monospace', fontSize: '10px', fontWeight: '600', padding: '5px 12px', borderRadius: '6px', border: `1px solid ${fc.selected.length > 0 ? '#2563EB' : border}`, background: fc.selected.length > 0 ? (darkMode ? 'rgba(37,99,235,0.15)' : '#DBEAFE') : surface2, color: fc.selected.length > 0 ? (darkMode ? '#93C5FD' : '#1D4ED8') : muted, cursor: 'pointer' }}>
                      {fc.label}{fc.selected.length > 0 ? ` (${fc.selected.length})` : ' ▾'}
                    </button>
                    {encOpenDropdown === fc.key && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 999, background: surface, border: `1px solid ${border}`, borderRadius: '8px', minWidth: '200px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', marginTop: '4px' }}
                        onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '6px 8px', borderBottom: `1px solid ${border}`, display: 'flex', gap: '6px' }}>
                          <button onClick={() => fc.setSelected(fc.allVals)} style={{ fontFamily: 'monospace', fontSize: '9px', padding: '2px 8px', borderRadius: '4px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>All</button>
                          <button onClick={() => fc.setSelected([])} style={{ fontFamily: 'monospace', fontSize: '9px', padding: '2px 8px', borderRadius: '4px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>Clear</button>
                        </div>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '4px' }}>
                          {fc.allVals.map(val => (
                            <div key={val} onClick={() => fc.setSelected(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val])}
                              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', borderRadius: '4px', cursor: 'pointer', background: fc.selected.includes(val) ? 'rgba(37,99,235,0.1)' : 'transparent' }}>
                              <div style={{ width: '12px', height: '12px', borderRadius: '3px', border: `1px solid ${fc.selected.includes(val) ? '#2563EB' : border}`, background: fc.selected.includes(val) ? '#2563EB' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {fc.selected.includes(val) && <span style={{ color: '#fff', fontSize: '9px', lineHeight: 1 }}>✓</span>}
                              </div>
                              <span style={{ fontSize: '11px', color: text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }} title={val}>{val}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {hasEncFilters && (
                  <button onClick={() => { setCompanyFilter([]); setInitiationFilter([]); setPayerEncFilter([]); setSpFilter([]); }}
                    style={{ fontFamily: 'monospace', fontSize: '9px', fontWeight: '700', padding: '4px 10px', borderRadius: '6px', border: '1px solid #EF4444', background: 'transparent', color: '#EF4444', cursor: 'pointer' }}>
                    ✕ Clear All
                  </button>
                )}
              </div>
            );
          })()}

          <div style={{ overflowX: 'auto', overflowY: 'auto', padding: '14px 20px 60px', flex: 1 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '80px', color: muted, fontFamily: 'monospace' }}>Loading records...</div>
            ) : records.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', gap: '14px', color: muted, textAlign: 'center' }}>
                <div style={{ fontSize: '44px', opacity: 0.2 }}>📋</div>
                <div style={{ fontFamily: 'monospace', fontSize: '15px', color: text }}>No data yet</div>
                <div style={{ fontSize: '13px', lineHeight: 1.7, maxWidth: '420px' }}>{isManager ? 'Click ⊕ Import CSV to get started.' : 'Waiting for manager to import data.'}</div>
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', color: muted, fontFamily: 'monospace' }}>No matching records</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                <thead>
                  <tr>
                    <th style={thStyle(border, surface2)} onClick={() => handleSort('_tag')}>Tag ⇅</th>
                    <th style={thStyle(border, surface2)} onClick={() => handleSort('__enc')}>Enc# {sortCol === '__enc' ? (sortDir > 0 ? '▲' : '▼') : '⇅'}</th>
                    {visibleCols.map(col => (
                      <th key={col} style={thStyle(border, surface2)} onClick={() => handleSort(col)}>{col} {sortCol === col ? (sortDir > 0 ? '▲' : '▼') : '⇅'}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => {
                    const tag = tags[r.__enc] || 0;
                    const tc = TC[tag];
                    return (
                      <tr key={r.__enc} style={{ borderLeft: `3px solid ${tc.border}` }}>
                        <td style={{ ...tdStyle(tc.bg, border), textAlign: 'center' }}>
                          {isManager ? (
                            <input type="number" min="0" max="6" value={tag || ''} placeholder="–" onChange={e => setTag(r.__enc, e.target.value)}
                              style={{ width: '44px', textAlign: 'center', fontFamily: 'monospace', fontWeight: '800', fontSize: '13px', background: 'rgba(0,0,0,0.25)', border: `1px solid ${tc.border}`, borderRadius: '5px', color: tc.color, padding: '3px', outline: 'none' }} />
                          ) : (
                            <span style={{ fontFamily: 'monospace', fontWeight: '800', fontSize: '13px', color: tc.color }}>{tag || '–'}</span>
                          )}
                        </td>
                        <td style={tdStyle(tc.bg, border)}>
                          <span style={{ fontFamily: 'monospace', fontSize: '11px', color: muted }}>{r.__enc}</span>
                          {importedNew.has(r.__enc) && <span style={{ fontFamily: 'monospace', fontSize: '9px', background: '#2563EB', color: '#fff', padding: '1px 5px', borderRadius: '3px', marginLeft: '5px' }}>NEW</span>}
                          {importedUpdated.has(r.__enc) && <span style={{ fontFamily: 'monospace', fontSize: '9px', color: '#F97316', marginLeft: '4px' }}>↑ UPDATED</span>}
                        </td>
                        {visibleCols.map(col => (
                          <td key={col} style={{ ...tdStyle(tc.bg, border), color: col === 'Value' ? (darkMode ? '#93C5FD' : '#1D4ED8') : tc.color, fontFamily: col === 'Value' ? 'monospace' : 'inherit', fontWeight: col === 'Value' ? '800' : 'normal' }}>
                            {r[col] || ''}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {showImport && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(4px)' }}
              onClick={e => { if (e.target === e.currentTarget) setShowImport(false); }}>
              <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: '10px', padding: '28px', width: '660px', maxWidth: '95vw' }}>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: '700', marginBottom: '6px', color: darkMode ? '#93C5FD' : '#1D4ED8' }}>⊕ Import Atlas CSV</div>
                <div style={{ color: muted, fontSize: '12px', marginBottom: '16px', lineHeight: 1.8 }}>Upload your CSV file or paste below. Tags matched by Enc# and never overwritten.</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); const file = e.dataTransfer.files[0]; if (file) { const reader = new FileReader(); reader.onload = ev => setPasteText(ev.target.result); reader.readAsText(file); } }}
                    style={{ border: `2px dashed ${border}`, borderRadius: '8px', padding: '20px', textAlign: 'center', color: muted, fontFamily: 'monospace', fontSize: '11px', cursor: 'pointer', background: bg }}
                    onClick={() => document.getElementById('csvFileInput').click()}>
                    {pasteText ? '✅ File loaded — click Import & Merge' : '📂 Click or drag & drop your CSV file here'}
                    <input id="csvFileInput" type="file" accept=".csv" style={{ display: 'none' }}
                      onChange={e => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onload = ev => setPasteText(ev.target.result); reader.readAsText(file); } }} />
                  </div>
                  <div style={{ textAlign: 'center', color: muted, fontSize: '11px', fontFamily: 'monospace' }}>— or paste manually —</div>
                  <textarea value={pasteText} onChange={e => setPasteText(e.target.value)} placeholder="Paste CSV here..."
                    style={{ width: '100%', height: '120px', background: bg, border: `1px solid ${border}`, borderRadius: '6px', color: text, fontFamily: 'monospace', fontSize: '11px', padding: '12px', resize: 'vertical', outline: 'none' }} />
                </div>
                {importMsg && (
                  <div style={{ fontFamily: 'monospace', fontSize: '12px', marginTop: '10px', padding: '10px 14px', borderRadius: '8px', background: importMsg.startsWith('✅') ? '#064E25' : '#450A0A', border: `1px solid ${importMsg.startsWith('✅') ? '#22C55E' : '#EF4444'}`, color: importMsg.startsWith('✅') ? '#6EE7A0' : '#FCA5A5' }}>
                    {importMsg}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '14px', justifyContent: 'flex-end' }}>
                  <button onClick={() => { setShowImport(false); setImportMsg(''); setPasteText(''); }} style={{ fontFamily: 'monospace', fontSize: '11px', padding: '7px 14px', borderRadius: '6px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={importCSV} disabled={importing} style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: '700', padding: '7px 14px', borderRadius: '6px', border: 'none', background: '#2563EB', color: '#fff', cursor: 'pointer', opacity: importing ? 0.6 : 1 }}>
                    {importing ? 'Importing...' : 'Import & Merge'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'referrals' && (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 115px)', overflow: 'hidden' }}>
          <div style={{ background: surface2, borderBottom: `1px solid ${border}`, padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button onClick={() => { setPeriodOffset(o => o - 1); setSelectedSP(null); }} style={{ fontFamily: 'monospace', fontSize: '14px', padding: '5px 12px', border: `1px solid ${border}`, borderRadius: '6px', background: surface, color: text, cursor: 'pointer' }}>←</button>
              <div style={{ fontFamily: 'monospace', fontSize: '13px', color: text, fontWeight: '600', minWidth: '170px', textAlign: 'center' }}>{label}</div>
              <button onClick={() => { setPeriodOffset(o => o + 1); setSelectedSP(null); }} style={{ fontFamily: 'monospace', fontSize: '14px', padding: '5px 12px', border: `1px solid ${border}`, borderRadius: '6px', background: surface, color: text, cursor: 'pointer' }}>→</button>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              {['month', 'quarter', 'year'].map(t => (
                <button key={t} onClick={() => { setPeriodType(t); setPeriodOffset(0); setSelectedSP(null); }}
                  style={{ fontFamily: 'monospace', fontSize: '10px', fontWeight: '700', padding: '5px 13px', border: `1px solid ${periodType === t ? '#2563EB' : border}`, borderRadius: '6px', background: periodType === t ? '#2563EB' : surface, color: periodType === t ? '#fff' : muted, cursor: 'pointer', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {!refLoading && sortedSP.length > 0 && (() => {
            const allIns = {};
            Object.values(curRefs).forEach(d => { Object.entries(d.insurance).forEach(([payer, count]) => { allIns[payer] = (allIns[payer] || 0) + count; }); });
            const totalVal = Object.values(curRefs).reduce((s, d) => s + d.value, 0);
            const prevTotal = Object.values(prevRefs).reduce((s, d) => s + d.refs, 0);
            const diff = totalRefs - prevTotal;
            const maxRefs = sortedSP[0] ? sortedSP[0][1].refs : 1;
            return (
              <div style={{ background: surface, borderBottom: `1px solid ${border}`, padding: '10px 20px', flexShrink: 0 }}>
                <div onClick={() => setOverviewCollapsed(c => !c)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: overviewCollapsed ? '0' : '10px' }}>
                  <div style={{ fontFamily: 'monospace', fontSize: '9px', color: muted, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '700' }}>Period overview · {label}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '10px', color: muted, background: surface2, border: `1px solid ${border}`, borderRadius: '4px', padding: '2px 8px' }}>{overviewCollapsed ? '▼ show' : '▲ hide'}</div>
                </div>
                {!overviewCollapsed && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '150px', flexShrink: 0 }}>
                      <div style={{ background: surface2, border: `1px solid ${border}`, borderRadius: '8px', padding: '10px 12px', flex: 1 }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '8px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '4px', fontWeight: '700' }}>Total referrals</div>
                        <div style={{ fontFamily: 'monospace', fontSize: '30px', fontWeight: '800', color: darkMode ? '#93C5FD' : '#1D4ED8', lineHeight: 1 }}>{totalRefs}</div>
                        <div style={{ fontFamily: 'monospace', fontSize: '12px', color: diff > 0 ? (darkMode ? '#22C55E' : '#15803D') : diff < 0 ? '#EF4444' : muted, marginTop: '4px' }}>
                          {diff > 0 ? `↑ +${diff} vs ${prevLabel}` : diff < 0 ? `↓ ${diff} vs ${prevLabel}` : `= same as ${prevLabel}`}
                        </div>
                      </div>
                      <div style={{ background: surface2, border: `1px solid ${border}`, borderRadius: '8px', padding: '10px 12px', flex: 1 }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '8px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '4px', fontWeight: '700' }}>Total value</div>
                        <div style={{ fontFamily: 'monospace', fontSize: '15px', fontWeight: '800', color: '#22C55E', lineHeight: 1 }}>{fmt(totalVal)}</div>
                        <div style={{ fontFamily: 'monospace', fontSize: '9px', color: muted, marginTop: '4px' }}>{sortedSP.length} reps</div>
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0, background: surface2, border: `1px solid ${border}`, borderRadius: '8px', padding: '10px 12px' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '8px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '700' }}>Insurance breakdown</div>
                      {Object.entries(allIns).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([payer, count], i) => {
                        const pct = Math.round((count / totalRefs) * 100);
                        return (
                          <div key={payer} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: i < 7 ? '7px' : 0 }}>
                            <div style={{ fontSize: '11px', color: text, width: '120px', flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={payer}>{payer}</div>
                            <div style={{ flex: 1, height: '4px', background: border, borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: BAR_COLORS[i % BAR_COLORS.length], borderRadius: '3px' }} />
                            </div>
                            <div style={{ fontFamily: 'monospace', fontSize: '10px', color: muted, width: '28px', textAlign: 'right', flexShrink: 0 }}>{pct}%</div>
                            <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: '800', color: darkMode ? '#93C5FD' : '#1D4ED8', width: '20px', textAlign: 'right', flexShrink: 0 }}>{count}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ flex: 1, background: surface2, border: `1px solid ${border}`, borderRadius: '8px', padding: '10px 12px', minWidth: 0 }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '8px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '700' }}>Referrals by rep</div>
                      {sortedSP.map(([name, data], i) => {
                        const pct = Math.round((data.refs / maxRefs) * 100);
                        return (
                          <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: i < sortedSP.length - 1 ? '6px' : 0 }}>
                            <div style={{ fontSize: '11px', color: text, width: '70px', flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={name}>{name.split(' ')[0]}{name.split(' ')[1] ? ' ' + name.split(' ')[1][0] + '.' : ''}</div>
                            <div style={{ flex: 1, height: '6px', background: border, borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: '#2563EB', borderRadius: '3px' }} />
                            </div>
                            <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: '800', color: darkMode ? '#93C5FD' : '#1D4ED8', width: '22px', textAlign: 'right', flexShrink: 0 }}>{data.refs}</div>
                          </div>
                        );
                      })}
                    </div>
                    {(() => {
                      const companyMap = {};
                      filterRefRecs(start, end).forEach(r => { const co = r.company || 'Unknown'; companyMap[co] = (companyMap[co] || 0) + 1; });
                      const sortedCo = Object.entries(companyMap).sort((a, b) => b[1] - a[1]);
                      const maxCo = sortedCo[0] ? sortedCo[0][1] : 1;
                      return (
                        <div style={{ flex: 1, background: surface2, border: `1px solid ${border}`, borderRadius: '8px', padding: '10px 12px', minWidth: 0 }}>
                          <div style={{ fontFamily: 'monospace', fontSize: '8px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '700' }}>Referrals by company</div>
                          {sortedCo.map(([name, count], i) => {
                            const pct = Math.round((count / maxCo) * 100);
                            return (
                              <div key={name} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: i < sortedCo.length - 1 ? '6px' : 0 }}>
                                <div style={{ fontSize: '11px', color: text, width: '110px', flexShrink: 0, whiteSpace: 'normal', lineHeight: 1.4, wordBreak: 'break-word' }} title={name}>{name}</div>
                                <div style={{ flex: 1, height: '6px', background: border, borderRadius: '3px', overflow: 'hidden', marginTop: '4px' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', background: '#22C55E', borderRadius: '3px' }} />
                                </div>
                                <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: '800', color: darkMode ? '#22C55E' : '#15803D', width: '22px', textAlign: 'right', flexShrink: 0, marginTop: '2px' }}>{count}</div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })()}

          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            <div style={{ width: '215px', borderRight: `1px solid ${border}`, overflow: 'hidden', flexShrink: 0, background: surface, display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: '9px', fontWeight: '700', color: muted, letterSpacing: '0.1em', textTransform: 'uppercase', borderBottom: `1px solid ${border}`, background: surface2 }}>Sales people</div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {refLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: muted, fontFamily: 'monospace', fontSize: '11px' }}>Loading...</div>
                ) : sortedSP.length === 0 ? (
                  <div style={{ padding: '30px 14px', textAlign: 'center', color: muted, fontSize: '12px' }}>No referrals this period</div>
                ) : sortedSP.map(([name, data]) => {
                  const prev = (prevRefs[name] || {}).refs || 0;
                  const diff = data.refs - prev;
                  const isActive = name === activeSPName;
                  return (
                    <div key={name} onClick={() => setSelectedSP(name)}
                      style={{ padding: '11px 14px', borderBottom: `1px solid ${border}`, cursor: 'pointer', background: isActive ? (darkMode ? 'rgba(37,99,235,0.1)' : '#DBEAFE') : 'transparent', borderLeft: `3px solid ${isActive ? '#2563EB' : 'transparent'}`, transition: 'background 0.1s' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: text }}>{name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '3px' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '11px', color: darkMode ? '#93C5FD' : '#1D4ED8' }}>{data.refs} ref{data.refs !== 1 ? 's' : ''}</span>
                        <span style={{ fontFamily: 'monospace', fontSize: '10px', color: diff > 0 ? (darkMode ? '#22C55E' : '#15803D') : diff < 0 ? '#EF4444' : muted }}>{diff > 0 ? `↑ +${diff}` : diff < 0 ? `↓ ${diff}` : '= same'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ padding: '12px 14px', borderTop: `1px solid ${border}`, background: surface2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <span style={{ fontFamily: 'monospace', fontSize: '10px', color: muted, letterSpacing: '0.05em', fontWeight: '600' }}>Total refs</span>
                <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: '800', color: darkMode ? '#93C5FD' : '#1D4ED8' }}>{totalRefs}</span>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '20px', background: bg }}>
              {!activeSPData ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px', gap: '12px', color: muted }}>
                  <div style={{ fontSize: '36px', opacity: 0.2 }}>📊</div>
                  <div>No referrals for this period</div>
                </div>
              ) : <>
                <div style={{ marginBottom: '18px' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: text }}>{activeSPName}</div>
                  <div style={{ fontSize: '11px', color: muted, fontFamily: 'monospace', marginTop: '4px', letterSpacing: '0.04em' }}>Sales Complex Mobility only · {label}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
                  {(() => {
                    const diff = activeSPData.refs - activeSPPrev;
                    return [
                      { label: 'Referrals', val: activeSPData.refs, big: true, sub: diff > 0 ? `↑ +${diff} vs ${prevLabel}` : diff < 0 ? `↓ ${diff} vs ${prevLabel}` : `= same as ${prevLabel}`, subcolor: diff > 0 ? (darkMode ? '#22C55E' : '#15803D') : diff < 0 ? '#EF4444' : muted },
                      { label: 'Dollar value', val: fmt(activeSPData.value), big: false, sub: 'of referrals this period', subColor: muted },
                      { label: 'Unique payers', val: Object.keys(activeSPData.insurance).length, big: true, sub: 'insurers', subColor: muted },
                    ];
                  })().map((m, i) => (
                    <div key={i} style={{ background: surface, border: `1px solid ${border}`, borderRadius: '8px', padding: '14px 16px' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '9px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '700' }}>{m.label}</div>
                      <div style={{ fontFamily: 'monospace', fontSize: m.big ? '26px' : '17px', fontWeight: '800', color: m.big ? text : (darkMode ? '#93C5FD' : '#1D4ED8'), lineHeight: 1 }}>{m.val}</div>
                      <div style={{ fontFamily: 'monospace', fontSize: '10px', color: m.subColor, marginTop: '6px' }}>{m.sub}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: '8px', padding: '12px 14px' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: '9px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px', fontWeight: '700' }}>This period · {label}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '22px', fontWeight: '800', color: darkMode ? '#93C5FD' : '#1D4ED8' }}>{activeSPData.refs}</div>
                    <div style={{ fontSize: '11px', color: muted, marginTop: '3px' }}>referrals · {fmt(activeSPData.value)}</div>
                  </div>
                  <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: '8px', padding: '12px 14px' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: '9px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px', fontWeight: '700' }}>Last period · {prevLabel}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '22px', fontWeight: '800', color: muted }}>{activeSPPrev}</div>
                    <div style={{ fontSize: '11px', color: muted, marginTop: '3px' }}>referrals</div>
                  </div>
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '9px', color: muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px', fontWeight: '700' }}>Insurance breakdown</div>
                <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: '8px', padding: '16px' }}>
                  {Object.entries(activeSPData.insurance).sort((a, b) => b[1] - a[1]).map(([payer, count], i, arr) => {
                    const pct = Math.round((count / activeSPData.refs) * 100);
                    return (
                      <div key={payer} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: i < arr.length - 1 ? '12px' : 0 }}>
                        <div style={{ fontSize: '12px', color: text, width: '175px', flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={payer}>{payer}</div>
                        <div style={{ flex: 1, height: '6px', background: surface2, borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: BAR_COLORS[i % BAR_COLORS.length], borderRadius: '3px' }} />
                        </div>
                        <div style={{ fontFamily: 'monospace', fontSize: '11px', color: muted, width: '36px', textAlign: 'right', flexShrink: 0 }}>{pct}%</div>
                        <div style={{ fontFamily: 'monospace', fontSize: '11px', color: darkMode ? '#93C5FD' : '#1D4ED8', width: '44px', textAlign: 'right', flexShrink: 0 }}>{count} ref</div>
                      </div>
                    );
                  })}
                </div>
                <RefRecordsTable refRecords={refRecords} activeSPName={activeSPName} start={start} end={end} surface={surface} surface2={surface2} border={border} text={text} muted={muted} />
              </>}
            </div>
          </div>
        </div>
      )}

      {showUserManagement && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, backdropFilter: 'blur(4px)' }}
          onMouseDown={e => { if (e.target === e.currentTarget) { setShowUserManagement(false); setInviteMsg(''); setInviteEmail(''); setConfirmRemoveUser(null); } }}>
          <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: '10px', padding: '28px', width: '520px', maxWidth: '95vw', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: '700', marginBottom: '6px', color: darkMode ? '#93C5FD' : '#1D4ED8' }}>👥 Team Members</div>
            <div style={{ color: muted, fontSize: '12px', marginBottom: '20px' }}>{user.company}</div>
            <div style={{ fontFamily: 'monospace', fontSize: '9px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px', fontWeight: '700' }}>Invite new user(s)</div>
            <div style={{ marginBottom: '8px' }}>
              <textarea
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="email1@example.com, email2@example.com"
                rows={3}
                style={{ width: '100%', background: bg, border: `1px solid ${border}`, borderRadius: '6px', padding: '8px 12px', color: text, fontFamily: 'monospace', fontSize: '12px', outline: 'none', resize: 'vertical' }}
                onFocus={e => e.target.style.borderColor = '#2563EB'}
                onBlur={e => e.target.style.borderColor = border}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
                <div style={{ fontFamily: 'monospace', fontSize: '10px', color: muted }}>
                  {inviteEmail.trim() ? `${inviteEmail.split(',').map(e => e.trim()).filter(e => e).length} email(s)` : 'Separate multiple emails with commas'}
                </div>
                <button
                  onClick={inviteUser}
                  disabled={inviteLoading || !inviteEmail.trim()}
                  style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: '700', padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#2563EB', color: '#fff', cursor: inviteLoading || !inviteEmail.trim() ? 'not-allowed' : 'pointer', opacity: inviteLoading || !inviteEmail.trim() ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                  {inviteLoading ? 'Sending...' : 'Send Invites'}
                </button>
              </div>
            </div>
            {inviteMsg && (
              <div style={{ fontFamily: 'monospace', fontSize: '11px', marginBottom: '16px', padding: '8px 12px', borderRadius: '6px', background: inviteMsg.startsWith('✅') ? '#064E25' : '#450A0A', border: `1px solid ${inviteMsg.startsWith('✅') ? '#22C55E' : '#EF4444'}`, color: inviteMsg.startsWith('✅') ? '#6EE7A0' : '#FCA5A5' }}>
                {inviteMsg}
              </div>
            )}
            {!inviteMsg && <div style={{ marginBottom: '16px' }} />}
            <div style={{ fontFamily: 'monospace', fontSize: '9px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px', fontWeight: '700' }}>Current users</div>
            <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: '8px', marginBottom: '20px', overflow: 'hidden' }}>
              {usersLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', color: muted, fontFamily: 'monospace', fontSize: '12px' }}>Loading...</div>
              ) : companyUsers.map((u, i) => (
                <div key={u.id}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: i < companyUsers.length - 1 && confirmRemoveUser?.id !== u.id ? `1px solid ${border}` : 'none' }}>
                    <div>
                      <div style={{ fontSize: '13px', color: text, fontFamily: 'monospace' }}>{u.email}</div>
                      <div style={{ fontSize: '10px', color: u.role === 'manager' ? (darkMode ? '#93C5FD' : '#1D4ED8') : '#22C55E', fontFamily: 'monospace', marginTop: '2px', fontWeight: '600' }}>{u.role}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {u.email !== user.email && (
                        <div style={{ fontSize: '10px', color: muted, fontFamily: 'monospace' }}>{u.auth_id ? '● Active' : '○ Pending'}</div>
                      )}
                      {u.email === user.email && (
                        <div style={{ fontSize: '10px', color: muted, fontFamily: 'monospace' }}>You</div>
                      )}
                      {u.email !== user.email && u.role !== 'manager' && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => apiCall('/email/invite', { method: 'POST', body: JSON.stringify({ email: u.email }) }).then(() => alert('Invite resent to ' + u.email))}
                            style={{ fontFamily: 'monospace', fontSize: '10px', fontWeight: '700', padding: '4px 10px', borderRadius: '5px', border: '1px solid #2563EB', background: 'transparent', color: '#2563EB', cursor: 'pointer' }}>
                            Resend
                          </button>
                          <button
                            onClick={() => setConfirmRemoveUser(confirmRemoveUser?.id === u.id ? null : u)}
                            style={{ fontFamily: 'monospace', fontSize: '10px', fontWeight: '700', padding: '4px 10px', borderRadius: '5px', border: '1px solid #EF4444', background: 'transparent', color: '#EF4444', cursor: 'pointer' }}>
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {confirmRemoveUser?.id === u.id && (
                    <div style={{ padding: '10px 14px', background: darkMode ? '#1A0A0A' : '#FEF2F2', borderTop: `1px solid #EF4444`, borderBottom: i < companyUsers.length - 1 ? `1px solid ${border}` : 'none' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '11px', color: darkMode ? '#FCA5A5' : '#B91C1C', marginBottom: '8px' }}>
                        Remove <strong>{u.email}</strong>? This cannot be undone.
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => setConfirmRemoveUser(null)} style={{ fontFamily: 'monospace', fontSize: '10px', padding: '4px 12px', borderRadius: '5px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>Cancel</button>
                        <button onClick={() => removeUser(u)} style={{ fontFamily: 'monospace', fontSize: '10px', fontWeight: '700', padding: '4px 12px', borderRadius: '5px', border: 'none', background: '#EF4444', color: '#fff', cursor: 'pointer' }}>Confirm Remove</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowUserManagement(false); setInviteMsg(''); setInviteEmail(''); setConfirmRemoveUser(null); }} style={{ fontFamily: 'monospace', fontSize: '11px', padding: '7px 14px', borderRadius: '6px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showChangePassword && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, backdropFilter: 'blur(4px)' }}
          onMouseDown={e => { if (e.target === e.currentTarget) { setShowChangePassword(false); setPasswordMsg(''); } }}>
          <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: '10px', padding: '28px', width: '420px', maxWidth: '95vw' }}>
            <div style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: '700', marginBottom: '6px', color: darkMode ? '#93C5FD' : '#1D4ED8' }}>⚙ Change Password</div>
            <div style={{ color: muted, fontSize: '12px', marginBottom: '20px' }}>Signed in as {user.email}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                { label: 'Current Password', val: currentPassword, set: setCurrentPassword, ph: 'Your current password' },
                { label: 'New Password', val: newPassword, set: setNewPassword, ph: 'Min 8 characters' },
                { label: 'Confirm New Password', val: confirmPassword, set: setConfirmPassword, ph: 'Repeat new password' },
              ].map(f => (
                <div key={f.label} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ color: muted, fontSize: '11px', fontFamily: 'monospace', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: '700' }}>{f.label}</label>
                  <input type="password" value={f.val} onChange={e => f.set(e.target.value)} placeholder={f.ph}
                    style={{ background: bg, border: `1px solid ${border}`, borderRadius: '6px', padding: '10px 14px', color: text, fontFamily: 'monospace', fontSize: '14px', outline: 'none' }}
                    onFocus={e => e.target.style.borderColor = '#2563EB'} onBlur={e => e.target.style.borderColor = border} />
                </div>
              ))}
              <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: '8px', padding: '10px 14px' }}>
                <div style={{ fontFamily: 'monospace', fontSize: '9px', color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '700' }}>Password requirements</div>
                {[
                  { label: '8+ characters', met: newPassword.length >= 8 },
                  { label: 'Uppercase letter', met: /[A-Z]/.test(newPassword) },
                  { label: 'Number', met: /[0-9]/.test(newPassword) },
                  { label: 'Special character (!@#$%)', met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword) },
                ].map((req, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: i < 3 ? '4px' : 0 }}>
                    <span style={{ color: req.met ? '#22C55E' : muted, fontSize: '11px' }}>{req.met ? '✓' : '○'}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '11px', color: req.met ? '#22C55E' : muted }}>{req.label}</span>
                  </div>
                ))}
                {confirmPassword.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <span style={{ color: newPassword === confirmPassword ? '#22C55E' : '#EF4444', fontSize: '11px' }}>{newPassword === confirmPassword ? '✓' : '✗'}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '11px', color: newPassword === confirmPassword ? '#22C55E' : '#EF4444' }}>{newPassword === confirmPassword ? 'Passwords match' : 'Passwords do not match'}</span>
                  </div>
                )}
              </div>
              {passwordMsg && (
                <div style={{ padding: '10px 14px', borderRadius: '8px', fontFamily: 'monospace', fontSize: '12px', background: passwordMsg.includes('✅') ? '#064E25' : '#450A0A', border: `1px solid ${passwordMsg.includes('✅') ? '#22C55E' : '#EF4444'}`, color: passwordMsg.includes('✅') ? '#6EE7A0' : '#FCA5A5' }}>
                  {passwordMsg}
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
                <button onClick={() => { setShowChangePassword(false); setPasswordMsg(''); setNewPassword(''); setConfirmPassword(''); setCurrentPassword(''); }}
                  style={{ fontFamily: 'monospace', fontSize: '11px', padding: '7px 14px', borderRadius: '6px', border: `1px solid ${border}`, background: surface2, color: text, cursor: 'pointer' }}>Cancel</button>
                <button onClick={changePassword} disabled={passwordLoading}
                  style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: '700', padding: '7px 14px', borderRadius: '6px', border: 'none', background: '#2563EB', color: '#fff', cursor: 'pointer', opacity: passwordLoading ? 0.6 : 1 }}>
                  {passwordLoading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAdminPanel && (
        <AdminPanel
          user={user}
          darkMode={darkMode}
          surface={surface}
          surface2={surface2}
          border={border}
          text={text}
          muted={muted}
          bg={bg}
          onClose={() => setShowAdminPanel(false)}
        />
      )}
    </div>
  );
}

function thStyle(border, bg) {
  return { fontFamily: 'monospace', fontSize: '9px', fontWeight: '800', letterSpacing: '0.09em', textTransform: 'uppercase', color: '#4B5578', padding: '8px 10px', borderBottom: `2px solid ${border}`, textAlign: 'left', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', background: bg };
}

function tdStyle(bg, border) {
  return { padding: '6px 10px', borderBottom: `1px solid ${border}`, fontSize: '12px', whiteSpace: 'nowrap', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', background: bg };
}